import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Edit, Trash2, Plus, Search, ChevronLeft, ChevronRight, Check, Eye, MoreVertical, X, ArrowUp, ArrowDown } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../lib/constants';
import { getCookie } from '../../lib/utils';
import { useNotification } from '../../contexts/NotificationContext';
import { AuthContext } from '../../contexts/AuthContext';
import { useRoleList } from './shared/services/useRoleService';

const ROWS_PER_PAGE = 10;

const RoleListPage = () => {
  const { userRole, permissions } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '0', 10);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  const hasPermission = (perm) => (userRole || '').toLowerCase() === 'admin' || (permissions || []).includes(perm);
  const showActionsColumn = hasPermission('Roles Edit') || hasPermission('Roles Delete');
  
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isDesktop = windowWidth >= 1024;

  const { roles, loading, fetchRoles, deleteRole } = useRoleList();

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedRole, setSelectedRole] = useState(null);
  const [modalData, setModalData] = useState({ name: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openModal = (mode, role = null) => {
    setModalMode(mode);
    setSelectedRole(role);
    setModalData({
      name: role ? role.name : '',
      description: role ? role.description : ''
    });
    setIsModalOpen(true);
  };

  const handleModalSave = async () => {
    if (!modalData.name.trim()) {
      showNotification('error', 'Role name is required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = getCookie('csrf_access_token');
      if (modalMode === 'create') {
        await axios.post(`${API_BASE_URL}/add_role`, {
          name: modalData.name,
          description: modalData.description,
          permissions: []
        }, { headers: { 'X-CSRF-TOKEN': token }, withCredentials: true });
        showNotification('success', 'New role created.');
      } else {
        // Fetch current permissions to preserve them
        const detailRes = await axios.get(`${API_BASE_URL}/get_role/${selectedRole.role_id}`, {
          withCredentials: true, headers: { 'X-CSRF-TOKEN': token }
        });
        const currentPerms = detailRes.data.data?.permissions || [];
        
        await axios.put(`${API_BASE_URL}/update_role/${selectedRole.role_id}`, {
          name: modalData.name,
          description: modalData.description,
          permissions: currentPerms
        }, { headers: { 'X-CSRF-TOKEN': token }, withCredentials: true });
        showNotification('success', 'Role identity updated.');
      }
      fetchRoles();
      setIsModalOpen(false);
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Action failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filtered = roles.filter(r =>
    (r.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Apply Sorting
  filtered.sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] || '';
    const bVal = b[sortConfig.key] || '';
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginated = filtered.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const resetSearch = () => { setSearchTerm(''); setSearchParams({ page: '0' }); };

  const handleDelete = (role) => {
    showNotification('confirm', `Delete role "${role.name}"? This may affect users assigned to this role.`, async () => {
      try {
        await deleteRole(role.role_id);
        showNotification('success', `Role "${role.name}" deleted successfully.`);
      } catch {
        showNotification('error', 'Failed to delete role.');
      }
    });
  };

  const ActionButton = ({ icon: Icon, label, onClick, color, bgColor, borderColor, tooltip }) => (
    <button
      onClick={onClick}
      title={tooltip || label}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        padding: '10px 16px', background: bgColor, color: color,
        border: `1px solid ${borderColor}`, borderRadius: '12px',
        fontSize: '12px', fontWeight: 800, cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
        flex: 1, textTransform: 'uppercase', letterSpacing: '0.02em'
      }}
      onMouseOver={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 6px 15px ${borderColor}40`;
      }}
      onMouseOut={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Icon style={{ width: 18, height: 18 }} />
      {label && <span>{label}</span>}
    </button>
  );

  if (loading) return (
    <div style={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{ height: 70, background: '#fff', border: '1px solid #f1f5f9', borderRadius: 12, animation: 'pulse 1.5s infinite' }} />
      ))}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: isDesktop ? '32px' : '16px' }}>
      
      {/* Unified Action Row: Search | Create */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>

        <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
          <Search style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setSearchParams({ page: '0' }); }}
            style={{
              width: '100%', padding: '12px 14px 12px 44px', border: '1px solid #e2e8f0',
              borderRadius: '14px', fontSize: '15px', outline: 'none',
              background: '#fff', color: '#1e293b', boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              boxSizing: 'border-box'
            }}
          />
          {searchTerm && (
            <button onClick={resetSearch} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
          )}
        </div>

        {hasPermission('Roles Add') && (
          <button
            onClick={() => openModal('create')}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: '#10b981', color: '#fff', border: 'none',
              borderRadius: '14px', padding: '12px 20px',
              fontSize: '14px', fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(16,185,129,0.2)',
              transition: 'all 0.2s', whiteSpace: 'nowrap'
            }}
            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Plus style={{ width: 18, height: 18 }} /> Create Role
          </button>
        )}
      </div>

      {/* Main List View */}
      {paginated.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '20px', border: '1px solid #e2e8f0', padding: '80px 24px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '20px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Shield style={{ width: 32, height: 32, color: '#cbd5e1' }} />
          </div>
          <h3 style={{ margin: 0, fontSize: '18px', color: '#475569', fontWeight: 700 }}>No roles found</h3>
          <p style={{ margin: '8px 0 24px', color: '#94a3b8', fontSize: '14px' }}>
            {searchTerm ? "Try adjusting your search terms" : "Get started by creating your first system role"}
          </p>
          {searchTerm ? (
            <button onClick={resetSearch} style={{ color: '#10b981', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '15px' }}>Clear search results</button>
          ) : (
            hasPermission('Roles Add') && (
              <button onClick={() => navigate('/addRole')} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '12px', padding: '12px 28px', cursor: 'pointer', fontWeight: 700, fontSize: '15px', boxShadow: '0 4px 12px rgba(16,185,129,0.2)' }}>Create Role</button>
            )
          )}
        </div>
      ) : isDesktop ? (
        /* TABLE VIEW FOR DESKTOP */
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {[
                  { label: 'Role Details', key: 'name' },
                  { label: 'Permissions', key: null },
                ].map(h => (
                  <th 
                    key={h.label} 
                    onClick={() => h.key && requestSort(h.key)}
                    style={{ 
                      padding: '16px 24px', fontSize: '12px', fontWeight: 700, color: '#64748b', 
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      cursor: h.key ? 'pointer' : 'default'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {h.label}
                      {sortConfig.key === h.key && (
                        sortConfig.direction === 'asc' ? <ArrowUp style={{ width: 12, height: 12 }} /> : <ArrowDown style={{ width: 12, height: 12 }} />
                      )}
                    </div>
                  </th>
                ))}
                {showActionsColumn && (
                  <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginated.map((role) => {
                const perms = Array.isArray(role.permissions) ? role.permissions : [];
                return (
                  <tr key={role.role_id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#fcfdfe'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '20px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{ width: 44, height: 44, borderRadius: '12px', background: '#f0fdf4', border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Shield style={{ width: 22, height: 22, color: '#10b981' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{role.name}</div>
                          <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '2px' }}>{role.description || 'No description provided'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '20px 24px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxWidth: '400px' }}>
                        {perms.slice(0, 3).map(p => (
                          <span key={p} style={{ background: '#f1f5f9', color: '#475569', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Check style={{ width: 10, height: 10, color: '#10b981' }} /> {p}
                          </span>
                        ))}
                        {perms.length > 3 && (
                          <span style={{ background: '#f0fdf4', color: '#10b981', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>+{perms.length - 3} more</span>
                        )}
                        {perms.length === 0 && <span style={{ color: '#cbd5e1', fontSize: '13px', fontStyle: 'italic' }}>No permissions assigned</span>}
                      </div>
                    </td>
                    {showActionsColumn && (
                      <td style={{ padding: '20px 24px', textAlign: 'right' }}>
                        <div className="flex gap-2 justify-end">
                          {hasPermission('Roles Edit') && (
                            <button 
                              onClick={() => openModal('edit', role)}
                              className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors font-medium text-sm"
                              title="Edit Role Identity"
                            >
                              <Edit className="h-4 w-4" />
                              Edit
                            </button>
                          )}
                          {hasPermission('Roles Delete') && (
                            <button 
                              onClick={() => handleDelete(role)}
                              className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors font-medium text-sm"
                              title="Delete Role"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* CARD VIEW FOR MOBILE */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {paginated.map((role) => (
            <div key={role.role_id} style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '10px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Shield style={{ width: 20, height: 20, color: '#10b981' }} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>{role.name}</h3>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>{role.description}</p>
                  </div>
                </div>
              </div>
              
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '12px', marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Permissions Overview</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {role.permissions?.slice(0, 4).map(p => (
                    <span key={p} style={{ background: '#fff', border: '1px solid #e2e8f0', color: '#475569', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Check style={{ width: 10, height: 10, color: '#10b981' }} /> {p}
                    </span>
                  ))}
                  {role.permissions?.length > 4 && <span style={{ padding: '2px 4px', fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>+{role.permissions.length - 4} more</span>}
                </div>
              </div>

              {showActionsColumn && (
                <div className="flex gap-2">
                  {hasPermission('Roles Edit') && (
                    <button 
                      onClick={() => openModal('edit', role)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Edit className="h-4 w-4" /> Edit
                    </button>
                  )}
                  {hasPermission('Roles Delete') && (
                    <button 
                      onClick={() => handleDelete(role)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Trash2 className="h-4 w-4" /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info Bar */}
      <div style={{ 
        marginTop: '24px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', 
        padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
      }}>
        <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
          Showing <strong>{filtered.length > 0 ? currentPage * ROWS_PER_PAGE + 1 : 0}</strong> to <strong>{Math.min((currentPage + 1) * ROWS_PER_PAGE, filtered.length)}</strong> of <strong>{filtered.length}</strong> roles
        </span>

        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button 
              disabled={currentPage === 0} 
              onClick={() => setSearchParams({ page: (currentPage - 1).toString() })}
              style={{
                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fff', 
                cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.5 : 1
              }}
            >
              <ChevronLeft style={{ width: 18, height: 18 }} />
            </button>
            
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setSearchParams({ page: i.toString() })}
                style={{
                  width: 36, height: 36, border: '1px solid', 
                  borderRadius: '10px', fontSize: '14px', fontWeight: 700,
                  cursor: 'pointer',
                  borderColor: currentPage === i ? '#10b981' : '#e2e8f0',
                  background: currentPage === i ? '#10b981' : '#fff',
                  color: currentPage === i ? '#fff' : '#64748b'
                }}
              >
                {i + 1}
              </button>
            ))}

            <button 
              disabled={currentPage >= totalPages - 1} 
              onClick={() => setSearchParams({ page: (currentPage + 1).toString() })}
              style={{
                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fff', 
                cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.5 : 1
              }}
            >
              <ChevronRight style={{ width: 18, height: 18 }} />
            </button>
          </div>
        )}
      </div>
      {/* Identity Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', 
          backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', 
          justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            background: '#fff', width: '100%', maxWidth: '480px', 
            borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            position: 'relative'
          }}>
            {/* Modal Header */}
            <div style={{ 
              background: '#10b981', padding: '24px', display: 'flex', 
              alignItems: 'center', justifyContent: 'space-between', color: '#fff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ padding: '8px', background: 'rgba(255,255,255,0.2)', borderRadius: '12px' }}>
                  <Shield style={{ width: 24, height: 24 }} />
                </div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>
                  {modalMode === 'create' ? 'Add New Role' : 'Update Role Identity'}
                </h2>
              </div>
              <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px' }}>
                <X style={{ width: 24, height: 24 }} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '32px' }}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
                  ROLE NAME <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter role name (e.g. Manager)"
                  value={modalData.name}
                  onChange={e => setModalData({...modalData, name: e.target.value})}
                  style={{
                    width: '100%', padding: '14px 16px', border: '1.5px solid #e2e8f0',
                    borderRadius: '14px', fontSize: '15px', fontWeight: 600, color: '#1e293b',
                    outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = '#10b981'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
                  DESCRIPTION
                </label>
                <textarea
                  placeholder="Optional: What is this role for?"
                  value={modalData.description}
                  onChange={e => setModalData({...modalData, description: e.target.value})}
                  rows={3}
                  style={{
                    width: '100%', padding: '14px 16px', border: '1.5px solid #e2e8f0',
                    borderRadius: '14px', fontSize: '14px', color: '#1e293b',
                    outline: 'none', transition: 'border-color 0.2s', resize: 'none', boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = '#10b981'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '16px' }}>
                <button
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    flex: 1, padding: '14px', borderRadius: '14px', border: '1.5px solid #e2e8f0',
                    background: '#fff', color: '#64748b', fontSize: '15px', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleModalSave}
                  disabled={isSubmitting}
                  style={{
                    flex: 1, padding: '14px', borderRadius: '14px', border: 'none',
                    background: isSubmitting ? '#6ee7b7' : '#10b981', color: '#fff', 
                    fontSize: '15px', fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 10px 15px -3px rgba(16,185,129,0.3)', transition: 'all 0.2s'
                  }}
                >
                  {isSubmitting ? 'Saving...' : (modalMode === 'create' ? 'Create Role' : 'Update Identity')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleListPage;
